import { PropsWithChildren, ReactNode } from 'react';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';

import { CircleCheckmarkIcon, CloseCircleIcon, HistoryIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { Skeleton } from './Skeleton';
import { Paragraph } from './Text';

const Container = styled(Box)`
	position: relative;
	flex-shrink: 0;
`;

const Overlay = styled(Flex)`
	position: absolute;
	bottom: -2px;
	right: -5px;
	width: 20px;
	height: 20px;
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.surface.white};
	align-items: center;
	justify-content: center;
	overflow: hidden;
	z-index: 1;
	pointer-events: none;
	box-shadow: 0 0 0 1.5px ${tokens.surface.white};

	svg {
		display: block;
		width: 20px;
		height: 20px;
	}
`;

const Circle = styled(Flex)<{ $color: string; $size?: number; $shadow?: boolean }>`
	background: ${(props) => props.theme.colors[props.$color]};
	min-width: ${(props) => (props.$size ? `${props.$size}px` : '16px')};
	height: ${(props) => (props.$size ? `${props.$size}px` : '16px')};
	border-radius: ${(props) => (props.$size ? `${props.$size / 2}px` : '8px')};
	box-shadow: ${(props) => (props.$shadow ? '0px 0px 0px 2px white' : 'none')};
	padding: 4px;
	justify-content: center;
	align-items: center;

	svg {
		display: block;

		path {
			fill: ${(props) => props.theme.colors.white};
		}
	}

	${Paragraph} {
		font-size: 12px;
		color: ${(props) =>
			props.$color === 'primary' ? props.theme.colors.white : props.theme.colors.black};
		font-weight: 700;
	}
`;

/**
 * A Status wraps its children in a container that also contains a status bubble
 * into the top-right corner.
 * @param props - props
 * @param props.badge - Badge
 * @returns Wrapped component
 */
function BadgeContainer(props: PropsWithChildren<BoxProps & { badge?: ReactNode }>) {
	const { badge, ...rest } = props;

	return (
		<Container {...rest}>
			{badge && <Overlay>{badge}</Overlay>}
			{props.children}
		</Container>
	);
}

export function Badge(props: { color: string; icon?: ReactNode; size?: number; shadow?: boolean }) {
	return (
		<Circle $color={props.color} $size={props.size} $shadow={props.shadow}>
			{props.icon}
		</Circle>
	);
}

Badge.Skeleton = styled(Skeleton.Box)<{ $size?: number }>`
	width: ${(props) => (props.$size ? `${props.$size}px` : '16px')};
	height: ${(props) => (props.$size ? `${props.$size}px` : '16px')};
`;

Badge.Container = BadgeContainer;

Badge.Success = styled(CircleCheckmarkIcon).attrs({
	solid: true,
	color: tokens.success.icon,
})``;

Badge.Pending = styled(HistoryIcon).attrs({
	solid: true,
	color: tokens.warning.icon,
})``;

Badge.Error = styled(CloseCircleIcon).attrs({
	solid: true,
	color: tokens.error.icon,
})``;
